import React from "react";
import { useTranslation } from 'react-i18next'
import { useSearchParams } from "react-router-dom";
import TopMenu from "../../components/TopMenu/TopMenu";
import BottomNav from "../../components/BottomNav/BottomNav";
import Table, { TableColumn } from "../../components/Table/Table";
import usePlantoes from "./usePlantoes";
import { formatDateTime } from "../../util/timezone.service";
import useTimezone from "../../hooks/useTimezone";
import "./Plantoes.css";
import { Plantao, vagasAbertas } from "../../models/Plantao";

export default function Plantoes() {
  const [searchParams] = useSearchParams();
  const { timezone } = useTimezone();
  const tipoParam = (searchParams.get("tipo") || "online").toLowerCase();
  const placeId = searchParams.get("placeId") || searchParams.get("cidadeId") || "";
  const selectedPlaceName = searchParams.get("placeName") || searchParams.get("cidadeId") || "";
  const modalidade = tipoParam === "presencial" ? "presencial" : "online";
  const isPresencial = modalidade === "presencial";
  const { plantoes, isLoading } = usePlantoes({ modalidade, placeId });
  const { t } = useTranslation();

  const titulo = isPresencial
    ? t("pages.plantoes.presencialTitle", { placeName: selectedPlaceName || "" })
    : t("pages.plantoes.onlineTitle");

  const columns: TableColumn<Plantao>[] = [
    {
      id: "data",
      header: t("pages.plantoes.data"),
      render: (plantao) => {
        console.warn(`Rendering date for plantao: ${plantao.start_date_utc}: timezone=${timezone}, locale=${t("current_locale")}, pattern=${t("pages.configuration.dateTimeFormats.long")}`);
        return formatDateTime(plantao.start_date_utc, t("pages.configuration.dateTimeFormats.shortWithWeekDay"), { timezone: timezone, locale: t("current_locale") })
      }
    },
    {
      id: "vagas",
      header: t("pages.plantoes.numeroVagas"),
      render: (plantao) => vagasAbertas(plantao)
    },
    {
      id: "situacao",
      header: t("pages.plantoes.situacao"),
      render: (plantao) => {
        const isCompleto = vagasAbertas(plantao) === 0;
        const statusLabel = isCompleto
          ? t("COMPLETE")
          : plantao.status
            ? t(plantao.status)
            : "—";

        return (
          <span className="plantoes-status">
            <span
              aria-hidden="true"
              className={`plantoes-status-dot ${isCompleto ? "is-complete" : "is-open"}`}
            />
            <span>{statusLabel}</span>
          </span>
        );
      }
    }
  ];

  return (
    <div className="plantoes-page">
      <TopMenu />

      <main className="plantoes-content">
        <section className={`plantoes-hero ${isPresencial ? "is-presencial" : "is-online"}`}>
          <p className="plantoes-hero-title">{titulo}</p>
          <p className="plantoes-hero-subtitle">
            {t("pages.plantoes.subtitle")}
          </p>
        </section>

        {isLoading ? (
          <section className="plantoes-loading-state" role="status" aria-live="polite">
            <span className="plantoes-loading-spinner" aria-hidden="true" />
            <span>{t("pages.plantoes.loading")}</span>
          </section>
        ) : plantoes.length === 0 ? (
          <section className="plantoes-empty-state" role="status" aria-live="polite">
            {t("pages.plantoes.emptyMessage")}
          </section>
        ) : (
          <Table<Plantao>
            bodyRowClassName="plantoes-row"
            columns={columns}
            data={plantoes}
            getRowKey={(plantao) => plantao.id}
            gridTemplateColumns="minmax(200px, 1fr) minmax(120px, 200px) minmax(120px, 160px)"
            headerRowClassName="plantoes-row plantoes-header"
            linkRowClassName="plantoes-link"
            rowHref={(plantao) => `/entrevistas?id=${plantao.id}&modalidade=${modalidade}${isPresencial ? `&placeId=${placeId}&placeName=${encodeURIComponent(selectedPlaceName)}` : ""}`}
            wrapperClassName="plantoes-table-wrapper"
          />
        )}
      </main>

      <BottomNav />
    </div>
  );
}
